import MouseMode from "../settings/MouseMode";
import React, {createRef, ReactNode, RefObject} from "react";
import ZoomMode from "../settings/ZoomMode";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import DocumentSettings from "../../document/DocumentSettings";
import NumberHelper from "../../../shared/helper/NumberHelper";
import DomHelper from "../../../shared/helper/DomHelper";
import {VIEWER_PADDING} from "../AbstractViewer";
import Observer from "../../../shared/class/Observer";
import LocationEvent from "../LocationEvent";
import PortalContext from "../../PortalContext";
import Immutable from "../../../shared/immutable/Immutable";
import RectSelection from "../../document/components/selection/RectSelection";
import {Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

interface ZoomLayerProperties extends I18nGlobals, DebugGlobals {
	pageNumber: number;
	zoomValue: number;
	context: PortalContext;
	mouseMode: MouseMode;
	locationObserver?: Observer<LocationEvent>;
	updateScroll: (xPos: number, yPos: number, pageNumber?: number) => Promise<void>;
	portalDocument: Immutable<PortalDocumentInterface>;
	attachmentKey?: string;
}

interface ZoomLayerStatus {
	zoomOut: boolean;
}

export class ZoomLayerDefinition extends PortalPureComponent<ZoomLayerProperties, ZoomLayerStatus> {
	private zoomLayer: RefObject<HTMLDivElement>;

	constructor(props: Readonly<ZoomLayerProperties>) {
		super(props);

		this.zoomLayer = createRef<HTMLDivElement>();
		this.state = {
			zoomOut: false
		};

		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleKeyup = this.handleKeyup.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);
		window.addEventListener('keyup', this.handleKeyup);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);
		window.removeEventListener('keyup', this.handleKeyup);
	}

	private handleKeydown(event: KeyboardEvent): void {
		if (event.key.toLowerCase() === "control" && !this.state.zoomOut) {
			this.setState({
				zoomOut: true
			});
		}
	}

	private handleKeyup(event: KeyboardEvent): void {
		if (event.key.toLowerCase() === "control" && this.state.zoomOut) {
			this.setState({
				zoomOut: false
			});
		}
	}

	private async zoomArea(rectangle: Rectangle | null): Promise<void> {
		if (!FunctionHelper.exists(rectangle) || !FunctionHelper.exists(this.zoomLayer.current)) {
			return;
		}

		let viewerElement: HTMLElement | null = DomHelper.parent(this.zoomLayer.current, ".viewer");
		if (!FunctionHelper.exists(viewerElement)) {
			return;
		}

		let viewerRect: DOMRect = viewerElement!.getBoundingClientRect();
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let padding: number = VIEWER_PADDING + VIEWER_PADDING + DomHelper.SCROLLBAR_SIZE;
		let zoomScale: number = 1.25; // default scale on click
		if (rectangle!.width! > 5 && rectangle!.width! > rectangle!.height!) {
			zoomScale = (viewerRect.width - padding) / rectangle!.width!;
		} else if (rectangle!.height! > 5) {
			zoomScale = (viewerRect.height - padding) / rectangle!.height!;
		}

		if (this.state.zoomOut) {
			zoomScale = 0.75;
		}

		let zoomResult: number = this.props.zoomValue * zoomScale;
		zoomResult = NumberHelper.parseInt(zoomResult, {min: 10, max: 400});

		viewerSettings.activeViewer.zoomMode = ZoomMode.CUSTOM;
		viewerSettings.activeViewer.zoomValue = zoomResult;

		this.props.context.documentManager.updateGlobalDocumentList({}, () => {
			let scrollScale: number = zoomResult / this.props.zoomValue;

			// x position of the zoom area on the page
			let scrollPosX: number = rectangle!.x! * scrollScale;
			// add half the width of the zoom area
			scrollPosX += (rectangle!.width! * scrollScale) / 2;
			// subtract half the viewer/viewport width
			scrollPosX -= (viewerRect.width / 2);

			// y position of the zoom area on the page
			let scrollPosY: number = rectangle!.y! * scrollScale;
			// add half the height of the zoom area
			scrollPosY += (rectangle!.height! * scrollScale) / 2;
			// subtract half the viewer/viewport height
			scrollPosY -= (viewerRect.height / 2);

			// wait for page zoom
			window.requestAnimationFrame(async () => {
				if (!FunctionHelper.exists(this.props.updateScroll)) {
					return;
				}

				await this.props.updateScroll(scrollPosX, scrollPosY, this.props.pageNumber);
			});
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let zoomLayerClasses: Array<string> = ["zoom-layer"];

		if (this.props.mouseMode === MouseMode.ZOOM) {
			zoomLayerClasses.push("is-visible");
		}

		if (this.state.zoomOut) {
			zoomLayerClasses.push("out");
		}

		return (
			<div className={zoomLayerClasses.join(" ")} ref={this.zoomLayer}>
				{FunctionHelper.exists(this.zoomLayer.current) &&
                    <RectSelection {...this.props} getParent={() => {
						return this.zoomLayer.current;
					}} mouseMode={MouseMode.ZOOM}
                                   endCallback={async (values) => {
									   await this.zoomArea(values);
								   }}
                    />
				}
			</div>
		);
	}
}

const ZoomLayer = React.memo(ZoomLayerDefinition,
	(prevProps: Readonly<ZoomLayerProperties>, nextProps: Readonly<ZoomLayerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		return true;
	}
);

export default ZoomLayer;